<script setup lang="ts">
import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
/**
 * Presentational "review and accept terms" card shared by the cluster-terms gate
 * (`educators/accept-terms`) and the per-school terms step (CP-10278). Each caller
 * owns its own i18n (`termsGate.*` vs `schoolTermsStep.*`) and link targets and
 * passes resolved strings/hrefs in; this component owns only the shared layout so
 * the two surfaces can't drift.
 */
interface Props {
  title: string;
  description: string;
  termsText: string;
  termsHref: string;
  privacyText: string;
  privacyHref: string;
  acceptText: string;
  accepting?: boolean;
  /** Blocks acceptance (e.g. the linked document is unavailable — CP-11268). */
  disabled?: boolean;
  /** Why acceptance is blocked; rendered as a warning above the button. */
  disabledNotice?: string | null;
  error?: string | null;
  testid?: string;
  acceptTestid?: string;
}

withDefaults(defineProps<Props>(), {
  accepting: false,
  disabled: false,
  disabledNotice: null,
  error: null,
  testid: undefined,
  acceptTestid: undefined,
});

defineEmits<{ accept: [] }>();
</script>

<template>
  <div class="mx-auto flex max-w-xl flex-col items-center px-4 py-12 text-center" :data-testid="testid">
    <Heading size="xl" as="h1">
      {{ title }}
    </Heading>
    <Text class="mt-4">
      {{ description }}
    </Text>
    <div v-if="$slots.default" class="mt-6">
      <slot />
    </div>
    <div class="mt-6 flex gap-8">
      <KUICoreLink
        :text="termsText"
        :to="termsHref"
        is-external
        class="underline"
      />
      <KUICoreLink
        :text="privacyText"
        :to="privacyHref"
        is-external
        class="underline"
      />
    </div>
    <KUICoreAlertMessage
      v-if="disabledNotice"
      variant="warning"
      :message="disabledNotice"
      class="mt-6"
      data-testid="terms-unavailable-notice"
    />
    <KUICoreAlertMessage
      v-if="error"
      variant="error"
      :message="error"
      class="mt-6"
    />
    <Button
      class="mt-8"
      :data-testid="acceptTestid"
      size="lg"
      :disabled="accepting || disabled"
      type="button"
      @click="$emit('accept')"
    >
      {{ acceptText }}
    </Button>
  </div>
</template>
